import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, Send } from 'lucide-react';
import { api } from '../lib/api';
import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TRIPADVISOR_URL = 'https://www.tripadvisor.com/Attraction_Review-g8627695-d13437753-Reviews-Travel_Guide_Costa_Rica-San_Pedro_de_Poas_Province_of_Alajuela.html';
const GOOGLE_REVIEW_URL = 'https://g.page/r/CQE9j7Ao2r5zEAE/review';

const SCALE5 = ['excelente', 'muy_buena', 'buena', 'regular', 'deficiente'];
const SAFETY_OPTS = ['si_completamente', 'si_mayoria', 'parcialmente', 'no'];
const RECOMMEND_OPTS = ['si_definitivamente', 'probablemente_si', 'no_seguro', 'probablemente_no'];

const QuestionBlock = ({ index, label, options, value, onChange, t }) => (
  <div className="space-y-3" data-testid={`survey-question-${index}`}>
    <label className="block text-base md:text-lg font-semibold text-[#1A1A1A]">
      <span className="text-[#1D4E41] mr-1">{index}.</span> {label}
    </label>
    <div className="grid gap-2">
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            data-testid={`q${index}-option-${opt}`}
            className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm md:text-base transition-all flex items-center gap-3 ${
              selected
                ? 'border-[#1D4E41] bg-[#E8F5E9] text-[#1D4E41] font-semibold'
                : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            <span
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                selected ? 'border-[#1D4E41]' : 'border-gray-300'
              }`}
            >
              {selected && <span className="w-2.5 h-2.5 rounded-full bg-[#1D4E41]" />}
            </span>
            {t(`survey.opt.${opt}`)}
          </button>
        );
      })}
    </div>
  </div>
);

const SurveyPage = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const langParam = searchParams.get('lang');
  const { t, language, setLang } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPublicCta, setShowPublicCta] = useState(false);

  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [q3, setQ3] = useState(null);
  const [q4, setQ4] = useState(null);
  const [q5, setQ5] = useState(null);
  const [comment, setComment] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (langParam === 'es' || langParam === 'en') {
      setLang(langParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langParam]);

  useEffect(() => {
    const fetchContext = async () => {
      if (!token) {
        setError('invalid');
        setLoading(false);
        return;
      }
      try {
        const data = await api.getSurveyContext(bookingId, token);
        setContext(data);
        if (data.already_submitted) setSubmitted(true);
      } catch (e) {
        setError(e.response?.status === 403 ? 'invalid' : 'notfound');
      } finally {
        setLoading(false);
      }
    };
    fetchContext();
  }, [bookingId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!q1 || !q2 || !q3 || !q4 || !q5) {
      setFormError(t('survey.answerRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.submitSurvey(bookingId, {
        token,
        q1_punctuality: q1,
        q2_driver: q2,
        q3_cleanliness: q3,
        q4_safety: q4,
        q5_recommend: q5,
        comment: comment.trim() || null,
        language,
      });
      setSubmitted(true);
      setShowPublicCta(!!res.show_public_review_cta);
    } catch (e) {
      if (e.response?.status === 409) {
        setSubmitted(true);
      } else {
        setFormError(e.response?.data?.detail || 'Error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Navbar />
        <div className="pt-32 pb-16 flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 text-[#1D4E41] animate-spin mb-4" />
          <p className="text-gray-500">{t('survey.loading')}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Navbar />
        <div className="pt-32 pb-16 flex flex-col items-center justify-center min-h-[60vh] px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2 text-center">{t('survey.invalidTitle')}</h2>
          <p className="text-gray-500 text-center max-w-md mb-6">{t('survey.invalidSubtitle')}</p>
          <Link to="/" className="h-12 px-6 bg-[#1D4E41] text-white rounded-full font-medium flex items-center">{t('survey.backHome')}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Navbar />
        <section className="pt-32 pb-16">
          <div className="container mx-auto px-4 md:px-6 max-w-2xl">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 md:p-12 text-center" data-testid="survey-thanks">
              <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#1D4E41]" />
              </div>

              {showPublicCta ? (
                <>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-3">{t('survey.thanks5Title')}</h1>
                  <p className="text-gray-600 leading-relaxed mb-8">{t('survey.thanks5Subtitle')}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href={TRIPADVISOR_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="share-tripadvisor"
                      className="h-12 px-6 bg-[#00aa6c] hover:bg-[#008f5a] text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-all"
                    >
                      {t('survey.shareTripadvisor')}
                    </a>
                    <a
                      href={GOOGLE_REVIEW_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="share-google"
                      className="h-12 px-6 bg-[#4285F4] hover:bg-[#3367D6] text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-all"
                    >
                      {t('survey.shareGoogle')}
                    </a>
                  </div>
                  <Link to="/" className="inline-block mt-6 text-sm text-gray-500 hover:text-[#1D4E41]">{t('survey.noThanks')}</Link>
                </>
              ) : context?.already_submitted ? (
                <>
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-3">{t('survey.alreadySubmittedTitle')}</h1>
                  <p className="text-gray-600 mb-8">{t('survey.alreadySubmittedSubtitle')}</p>
                  <Link to="/" className="h-12 inline-flex items-center px-6 bg-[#1D4E41] text-white rounded-full font-medium">{t('survey.backHome')}</Link>
                </>
              ) : (
                <>
                  <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-3">{t('survey.thanksLowTitle')}</h1>
                  <p className="text-gray-600 mb-8">{t('survey.thanksLowSubtitle')}</p>
                  <Link to="/" className="h-12 inline-flex items-center px-6 bg-[#1D4E41] text-white rounded-full font-medium">{t('survey.backHome')}</Link>
                </>
              )}
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <section className="pt-28 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-2xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-3">{t('survey.title')}</h1>
            <p className="text-gray-600">{t('survey.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-10 space-y-8" data-testid="survey-form">
            <div className="text-center pb-6 border-b border-gray-100">
              <p className="text-gray-500 text-sm">
                {t('survey.hello')}, <strong className="text-[#1D4E41]">{context?.customer_name}</strong>
              </p>
              {context?.tour_title && (
                <p className="text-gray-500 text-sm mt-1">
                  {t('survey.thanksFor')} <strong>{context?.tour_title}</strong>
                </p>
              )}
            </div>

            <QuestionBlock index={1} label={t('survey.q1')} options={SCALE5} value={q1} onChange={setQ1} t={t} />
            <QuestionBlock index={2} label={t('survey.q2')} options={SCALE5} value={q2} onChange={setQ2} t={t} />
            <QuestionBlock index={3} label={t('survey.q3')} options={SCALE5} value={q3} onChange={setQ3} t={t} />
            <QuestionBlock index={4} label={t('survey.q4')} options={SAFETY_OPTS} value={q4} onChange={setQ4} t={t} />
            <QuestionBlock index={5} label={t('survey.q5')} options={RECOMMEND_OPTS} value={q5} onChange={setQ5} t={t} />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('survey.commentLabel')}</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={2000}
                data-testid="comment-input"
                placeholder={t('survey.commentPlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1D4E41] focus:ring-1 focus:ring-[#1D4E41] outline-none resize-none"
              />
            </div>

            {formError && (
              <div className="px-4 py-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2" data-testid="survey-form-error">
                <AlertCircle className="w-4 h-4" /> {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="survey-submit"
              className="w-full h-14 bg-[#1D4E41] hover:bg-[#153A30] disabled:opacity-50 text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" />{t('survey.submitting')}</> : <><Send className="w-5 h-5" />{t('survey.submitBtn')}</>}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default SurveyPage;
